import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Upload } from "../../../common/svg";
import SecureImage from "../../../components/BucketImage/Image";

interface UploadedImage {
  url: string;
  name: string;
}

interface Document {
  id: number;
  name: string;
  preview: string;
  file: File;
  type: string;
}

interface DocumentComponentProps {
  uploadedImages: UploadedImage[];
  setUploadedImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  toggleModal: () => void;
}

const UploadDocumentsModal: React.FC<DocumentComponentProps> = ({
  uploadedImages,
  setUploadedImages,
  files,
  setFiles,
  toggleModal,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);

  const handleNameChange = (id: number, newName: string) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, name: newName } : doc))
    );
  };

  const validationSchema = Yup.object({
    documents: Yup.array()
  });

  const formik = useFormik({
    initialValues: { documents: [] },
    validationSchema,
    onSubmit: (values) => {
      // Create new image objects from documents
      const newImages = documents.map((doc) => ({
        url: doc.preview,
        name: doc.name,
      }));
      
      // Update parent state with new images and files
      setUploadedImages([...uploadedImages, ...newImages]);
      setFiles([...files, ...documents.map((doc) => doc.file)]);
      
      console.log("Form submitted with documents:", documents);
      
      // Close the modal
      closeModal();
    },
  });

  const getFileType = (file: File): string => {
    if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type === 'application/pdf') {
      return 'pdf';
    } else {
      return 'other';
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);

      const uploadedFiles = selectedFiles.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        preview: URL.createObjectURL(file),
        file,
        type: getFileType(file)
      }));

      const existingFileNames = [...files.map((f) => f.name)];
      const newFiles = uploadedFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );

      if (uploadedFiles.length !== newFiles.length) {
        alert("Duplicate files detected. Only unique files were added.");
      }

      setDocuments((prev) => [...prev, ...newFiles]);
      formik.setFieldValue("documents", [...formik.values.documents, ...newFiles]);
    }
  };

  const handleRemoveDocument = (id: number) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    formik.setFieldValue(
      "documents",
      formik.values.documents.filter((doc: any) => doc.id !== id)
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    toggleModal();
  };

  const handleSubmit = () => {
    // Directly submit form
    formik.handleSubmit();
  };

  // Render document preview based on file type
  const renderDocumentPreview = (doc: Document) => {
    switch (doc.type) {
      case 'image':
        return (
          <img
            src={doc.preview}
            alt={doc.name}
            className="w-full h-28 object-cover rounded-md"
          />
        );
      case 'pdf':
        return (
          <object
            data={doc.preview}
            type="application/pdf"
            className="w-full h-28 rounded-md"
          >
            <div className="w-full h-28 flex items-center justify-center bg-gray-100 rounded-md">
              <span className="text-xs text-gray-700">PDF Preview</span>
            </div>
          </object>
        );
      default:
        // For other file types, show an icon or generic preview
        const extension = getFileExtension(doc.name);
        return (
          <div className="w-full h-28 flex flex-col items-center justify-center bg-gray-100 rounded-md">
            <div className="text-2xl mb-1">📄</div>
            <span className="text-xs text-gray-700 uppercase">{extension}</span>
          </div>
        );
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[707px] h-[490px] p-6 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-poppins font-[600] text-[16px]">Upload Documents</h2>
          <button
            className="text-dark-400 hover:text-dark-600 text-[25px]"
            onClick={closeModal}
          >
            &times;
          </button>
        </div>

        <div className="flex-grow overflow-hidden">
          <div className="h-full max-h-[300px] overflow-y-auto">
            <div className="grid grid-cols-3 gap-4 p-2">
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center h-[140px] cursor-pointer border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors duration-200"
              >
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                />
                <SecureImage fileKey={Upload} alt="upload icon" width="35px" />
                <span className="mt-2 font-poppins text-gray-600">Upload Documents</span>
              </label>

              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="relative border rounded-lg p-2 flex flex-col items-center h-[140px]"
                >
                  {renderDocumentPreview(doc)}
                  <input
                    type="text"
                    value={doc.name}
                    onChange={(e) => handleNameChange(doc.id, e.target.value)}
                    className="mt-2 text-sm text-center text-gray-700 border border-gray-300 rounded-md p-1 w-full"
                  />
                  <button
                    type="button"
                    className="absolute top-2 right-2 bg-gray-500 text-white text-xs rounded-[30px] h-[20px] w-[20px]"
                    onClick={() => handleRemoveDocument(doc.id)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-auto pt-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleSubmit}
            className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200"
            disabled={documents.length === 0}
          >
            Save
          </button>
          <button
            type="button"
            className="w-36 h-10 rounded-lg bg-white text-gray-800 border border-gray-300 hover:border-gray-400 transition duration-200"
            onClick={closeModal}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentsModal;